import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, videos } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === currentSlug) || categories[0],
    [currentSlug]
  );

  const filteredVideos = useMemo(() => {
    let result =
      currentSlug === 'all'
        ? [...videos]
        : videos.filter((v) => v.categorySlug === currentSlug);

    if (sortBy === 'hot') {
      result.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
    } else if (sortBy === 'new') {
      result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    } else if (sortBy === 'rating') {
      result.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    }
    return result;
  }, [currentSlug, sortBy]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 鲍鱼tv`,
    description: `鲍鱼tv${currentCategory.name}分类，海量高清${currentCategory.name}内容在线观看`,
    url: `https://www.baoyutv.com/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '鲍鱼tv',
      url: 'https://www.baoyutv.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          {/* Category Header */}
          <section className="w-full py-8 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <span className="text-sm text-foreground-400">
                  共 {filteredVideos.length} 部
                </span>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full bg-background-50 border-b border-background-200/70 overflow-x-auto">
            <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center gap-1 py-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* Sort Controls */}
        <section className="w-full py-4 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {[
                { key: 'hot' as const, label: '热门', icon: 'ri-fire-line' },
                { key: 'new' as const, label: '最新', icon: 'ri-sparkling-line' },
                { key: 'rating' as const, label: '评分', icon: 'ri-star-line' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-secondary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${s.icon} text-sm`}></i>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full py-8 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop="">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-film-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors"
                >
                  浏览全部内容
                </Link>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}